import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AppService } from 'app/core/app/app.service';
import {
  ProjectNameDialogComponent,
  ProjectNameDialogData,
} from 'app/core/components/project-name-dialog/project-name-dialog.component';
import { DialogService } from 'app/core/dialogs/dialog.service';
import { isSectionEnabled } from 'app/core/environment/build-features';
import { ProjectsClient } from 'app/core/projects/projects.client';

/**
 * Starts creating a project from wherever the user asks for one — the project
 * list and the header's project switcher share this flow.
 */
@Injectable({ providedIn: 'root' })
export class ProjectCreationService {
  private readonly _appService = inject(AppService);
  private readonly _dialogService = inject(DialogService);
  private readonly _matDialog = inject(MatDialog);
  private readonly _projects = inject(ProjectsClient);
  private readonly _router = inject(Router);

  /** Whether the current backend can create projects at all. */
  readonly canCreate = this._projects.capabilities.canCreate;

  /**
   * Whether this build registers the full-page create flow at `/projects/new`,
   * which offers product activation and is therefore Payment-only — the same
   * question `sectionEnabledGuard('project-creation')` asks on that route.
   *
   * When it does not, creating still works: Fleet takes nothing but a name, so
   * a dialog asks for it instead of navigating to a page that would only be
   * blocked by the guard.
   */
  readonly usesCreatePage = isSectionEnabled('project-creation');

  /**
   * Start creating a project, optionally with a suggested name (for example
   * what was typed into a search that found nothing).
   */
  start(initialName?: string): void {
    const name = initialName?.trim() || undefined;

    if (this.usesCreatePage) {
      void this._router.navigate(['/projects', 'new'], {
        queryParams: name ? { name } : {},
      });
      return;
    }

    void this._createFromDialog(name);
  }

  /**
   * Name-only create flow, used where `/projects/new` is not registered.
   *
   * Refetches the list before navigating so the new project is in `apps$` by
   * the time `appContextResolver` looks for it — it redirects back to
   * `/projects` for an id it cannot find.
   */
  private async _createFromDialog(initialName?: string): Promise<void> {
    const data: ProjectNameDialogData = {
      title: 'New project',
      confirmLabel: 'Create',
      initialName,
    };
    const name = await firstValueFrom(
      this._matDialog.open(ProjectNameDialogComponent, { data }).afterClosed(),
    );
    if (!name) {
      return;
    }

    try {
      const created = await firstValueFrom(this._projects.createProject(name));
      const apps = await firstValueFrom(this._appService.getApps());
      // Prefer the entry from the refetched list: Fleet's create response
      // describes the app before its state has materialised (`billingState`
      // and `status` come back `null`), so the listed one is the complete one.
      this._appService.setActiveApp(
        apps.find((app) => app.id === created.id) ?? created,
      );
      void this._router.navigate(['/projects', created.id]);
    } catch (error) {
      this._dialogService.showErrorMessage('Failed to create project', error);
    }
  }
}
